import {AxiosRequestConfig} from 'axios'
import axios from '../axiosConfig'
import {createAsyncThunk} from '@reduxjs/toolkit'
import {FetchTeaser} from '../types/teaser'
import {QueryParams} from '../common/configuration'
import {store} from './store'
import {FetchStage} from '../types/stage'
import {Dealer, FetchDealer} from '../types/dealer'
import {BackendEndpoints} from '../utils/url/BackendEndpoints'
import {Params} from '../shared/variables'
import {DEFAULT_ACTIVITY_TYPE, DEFAULT_CONTRACT_TYPE} from '../shared/utilities'
import {RootState} from './rootReducer'
import {getV1BaseFromBackendUrl} from '../utils/url/getV1BaseFromBackendUrl'
import addTrailingSlash from '../utils/url/addTrailingSlash'

export type AppDispatch = typeof store.dispatch

export interface FetchData {
    ENDPOINT: string
    TOKEN: string
}

const config: AxiosRequestConfig = {}

export const fetchAllModelsWithActivePromotions = createAsyncThunk(
    'garage/fetchAll',
    async (fetchAllData: FetchData) => {
        if (
            fetchAllData.ENDPOINT.includes(QueryParams.PREVIEW_DATE) &&
            fetchAllData.TOKEN.length
        ) {
            config.headers = {
                Authorization: `Bearer ${fetchAllData.TOKEN}`,
            }
            config.withCredentials = false
        }

        const response = await axios.get<string>(fetchAllData.ENDPOINT, config)

        return response.data
    }
)

export const fetchCarBySlug = createAsyncThunk(
    'garage/fetchModel',
    async (singleCarEndpoint: FetchData) => {
        if (
            singleCarEndpoint.ENDPOINT.includes(QueryParams.PREVIEW_DATE) &&
            singleCarEndpoint.TOKEN.length
        ) {
            config.headers = {
                Authorization: `Bearer ${singleCarEndpoint.TOKEN}`,
            }
            config.withCredentials = false
        }

        const response = await axios.get<string>(
            singleCarEndpoint.ENDPOINT,
            config
        )

        return response.data
    }
)

export const fetchAllTeasers = createAsyncThunk(
    'dealers/fetchAllTeasers',
    async (teaserEndpoint: FetchTeaser) => {
        const response = await axios.get<string>(teaserEndpoint.ENDPOINT)

        return response.data
    }
)

export const fetchAllStages = createAsyncThunk(
    'stages/fetchAllStages',
    async (stageEndpoint: FetchStage) => {
        const response = await axios.get<string>(stageEndpoint.ENDPOINT)

        return response.data
    }
)

export const getDealerName = createAsyncThunk(
    'dealerData/getDealerName',
    async (dealerEndpoint: FetchDealer) => {
        const response = await axios.get<Dealer[]>(dealerEndpoint.ENDPOINT)

        return response.data[0].company
    }
)

export const fetchAllProvinces = createAsyncThunk(
    'provinces/fetchAllProvinces',
    async (_, {getState}) => {
        const state = getState() as RootState
        const backendUrl = state.general.backendUrl

        const baseUrl = addTrailingSlash(backendUrl)

        const response = await axios.get<string[]>(
            `${baseUrl}${BackendEndpoints.DEALERS}/${BackendEndpoints.PROVINCES}`
        )

        return response.data
    }
)
export const fetchAllDealers = createAsyncThunk(
    'dealers/fetchAllDealers',
    async (_, {getState}) => {
        const state = getState() as RootState
        const backendUrl = state.general.backendUrl

        const baseUrl = addTrailingSlash(backendUrl)

        const response = await axios.get<Dealer[]>(
            `${baseUrl}${BackendEndpoints.DEALERS}`
        )

        return response.data
    }
)

export const fetchMyDealerBranches = createAsyncThunk(
    'dealers/fetchMyDealerBranches',
    async (dealerCode: string, {getState}) => {
        const state = getState() as RootState
        const backendUrl = state.general.backendUrl
        const brand = state.general.brand

        const v1BaseUrl = getV1BaseFromBackendUrl(backendUrl)

        const baseUrl = `${v1BaseUrl}/${BackendEndpoints.DEALERS}?${Params.BRAND}=${brand}&${Params.ACTIVITY_TYPE}=${DEFAULT_ACTIVITY_TYPE}&${Params.CONTRACT_TYPE}=${DEFAULT_CONTRACT_TYPE}&${Params.ORDER_BY}=${Params.CLEANED_COMPANY}`

        const bearerUrl = `${baseUrl}&${Params.BEARER}=${dealerCode}`

        return fetchDealers(bearerUrl, dealerCode)
    }
)

const fetchDealers = async (
    url: string,
    fallbackCode?: string
): Promise<Dealer[]> => {
    const response = await axios.get<Dealer[]>(url)

    if (
        Array.isArray(response.data) &&
        response.data.length === 0 &&
        fallbackCode
    ) {
        const fallbackUrl = url.replace(
            `${Params.BEARER}=${fallbackCode}`,
            `${Params.COMPANY_CODE}=${fallbackCode}`
        )
        const fallbackResponse = await axios.get<Dealer[]>(fallbackUrl)

        return fallbackResponse.data
    }

    return response.data
}
